import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { noop } from 'lodash';

import { FETCHING_STATUSES } from 'constants/globals';
import * as apiV6 from 'lib/apiV6';
import { getErrorHandler } from 'slices/app';

const SLICE_NAME = 'telegramManager';

export type TelegramManagerState = {
  cards: {
    fetchingStatus: $Values<typeof FETCHING_STATUSES>;
    items: AH$TelegramManager[];
    page: API$Pages;
  };
  fetchingStatus: $Values<typeof FETCHING_STATUSES>;

  phone: string;
};

export const telegramManagerInitialState: TelegramManagerState = {
  phone: '+7',
  fetchingStatus: 'SUCCESS',
  cards: { fetchingStatus: 'SUCCESS', items: [], page: {} as API$Pages },
};

export const getCode = createAsyncThunk(
  `${SLICE_NAME}/getCode`,
  async (
    {
      phoneNumber,
      onSuccess = noop,
      onError,
    }: { onError: (error: string) => void; phoneNumber: string; onSuccess?: () => void },
    { rejectWithValue }
  ) => {
    try {
      await apiV6.telegramSendCode(phoneNumber);
      onSuccess();

      return phoneNumber;
    } catch (e) {
      getErrorHandler({
        parseError: true,
        silent: true,
        getError: (error: Record<string, any>) => {
          try {
            const parsedError = JSON.parse(error.error);

            if (parsedError.error === 'TelegramUnknownError') {
              onError(parsedError.message);
            } else {
              onError(parsedError.error);
            }
          } catch (e) {
            if ('error' in error) {
              onError(error.error);
            } else {
              const firstKey = Object.keys(error)[0];
              const firstError = error[firstKey][0];

              onError(firstError);
            }
          }
        },
      })(e);

      return rejectWithValue(e);
    }
  }
);

export const addManager = createAsyncThunk(
  `${SLICE_NAME}/addManager`,
  async (
    {
      phoneNumber,
      code,
      onSuccess = noop,
      onError = noop,
    }: {
      code: string;
      onError: (error: string) => void;
      phoneNumber: string;
      onSuccess?: () => void;
    },
    { rejectWithValue }
  ) => {
    try {
      await apiV6.telegramConnectAccount({ code, phone_number: phoneNumber });
      onSuccess();
    } catch (e) {
      getErrorHandler({
        parseError: true,
        silent: true,
        getError: (error: Record<string, any>) => {
          try {
            const parsedError = JSON.parse(error.error);

            if (parsedError.error === 'TelegramUnknownError') {
              onError(parsedError.message);
            } else {
              onError(parsedError.error);
            }
          } catch (e) {
            if ('error' in error) {
              onError(error.error);
            } else {
              const firstKey = Object.keys(error)[0];
              const firstError = error[firstKey][0];

              onError(firstError);
            }
          }
        },
      })(e);

      rejectWithValue(e);
    }
  }
);

export const getSimCards = createAsyncThunk(
  `${SLICE_NAME}/getSimCards`,
  async (params: AH$TelegramAccountsParams, { rejectWithValue }) => {
    try {
      const { payload } = await apiV6.getAllTelegramAccounts(params);

      const page: API$Pages = {
        count: payload.count,
        next: payload?.next,
        previous: payload?.previous,
      };

      return { simCards: payload.results, page };
    } catch (e) {
      getErrorHandler()(e);
      return rejectWithValue(e);
    }
  }
);

export const deleteSimCard = createAsyncThunk(
  `${SLICE_NAME}/deleteSimCard`,
  async (
    {
      id,
      onSuccess = noop,
      onError = noop,
    }: { id: number; onError?: () => void; onSuccess?: () => void },
    { rejectWithValue }
  ) => {
    try {
      await apiV6.deleteTelegramAccount(id);

      onSuccess();
      return { id };
    } catch (e) {
      getErrorHandler()(e);
      onError();
      return rejectWithValue(e);
    }
  }
);

export const updateLimitSimCard = createAsyncThunk(
  `${SLICE_NAME}/updateLimitSimCard`,
  async (
    {
      id,
      limit_total,
      onSuccess = noop,
      onError = noop,
    }: {
      id: number;
      limit_total: number;
      onError?: () => void;
      onSuccess?: () => void;
    },
    { rejectWithValue }
  ) => {
    try {
      const { payload } = await apiV6.updateTelegramAccount({ id, limit_total });

      onSuccess();

      return payload;
    } catch (e) {
      getErrorHandler()(e);
      onError();
      return rejectWithValue(e);
    }
  }
);

const telegramManagerSlice = createSlice({
  name: SLICE_NAME,
  initialState: telegramManagerInitialState,
  reducers: {
    clearPhone: (state) => {
      state.phone = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCode.pending, (state) => {
      state.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(getCode.fulfilled, (state, action) => {
      state.fetchingStatus = FETCHING_STATUSES.SUCCESS;
      state.phone = action.payload;
    });
    builder.addCase(getCode.rejected, (state) => {
      state.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(addManager.pending, (state) => {
      state.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(addManager.fulfilled, (state) => {
      state.fetchingStatus = FETCHING_STATUSES.SUCCESS;
    });
    builder.addCase(addManager.rejected, (state) => {
      state.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(getSimCards.pending, (state) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(getSimCards.fulfilled, (state, action) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.SUCCESS;
      state.cards.items = action.payload.simCards;
      state.cards.page = action.payload.page;
    });
    builder.addCase(getSimCards.rejected, (state) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(deleteSimCard.pending, (state) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(deleteSimCard.fulfilled, (state, action) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.SUCCESS;
      state.cards.items = state.cards.items.filter((item) => item.id !== action.payload.id);
    });
    builder.addCase(deleteSimCard.rejected, (state) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
    builder.addCase(updateLimitSimCard.pending, (state) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.LOADING;
    });
    builder.addCase(updateLimitSimCard.fulfilled, (state, action) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.SUCCESS;
      state.cards.items = state.cards.items.reduce(
        (acc, item) => (item.id === action.payload.id ? [...acc, action.payload] : [...acc, item]),
        [] as AH$TelegramManager[]
      );
    });
    builder.addCase(updateLimitSimCard.rejected, (state) => {
      state.cards.fetchingStatus = FETCHING_STATUSES.ERROR;
    });
  },
});

export const { clearPhone } = telegramManagerSlice.actions;
export const telegramManager = telegramManagerSlice.reducer;
